import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import AdyenCheckout from '@adyen/adyen-web';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import queryString from 'query-string';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import padStart from 'lodash/padStart';
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl';
import { Modal, ModalBody } from 'reactstrap';
import { v4 as uuidV4 } from 'uuid';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation, faXmark } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';

import '@adyen/adyen-web/dist/adyen.css';

import env from 'Env';
import { currencyCode } from 'Helpers/Geo/data';
import { getMethods, removeMethod } from 'Redux/ducks/saved-payments';
import GeneralUtils from 'Helpers/GeneralUtils';
import Loading from 'Components/Utility/Loading';
import MessageBox from 'Components/Utility/MessageBox';
import PaymentContext from 'Components/Payment/payment-context';
import PaymentError from 'Components/Payment/PaymentError';
import PaymentMethod from 'Components/Payment/PaymentMethod';
import SavedPaymentMethod from 'Components/Account/PaymentInformation/SavedPaymentMethod';

import 'Components/Payment/PaymentMethod.scss';

const messages = defineMessages({
  confirmDelete: {
    id: 'account-saved-method.confirm-delete',
    defaultMessage: 'Are you sure you want to remove your {paymentMethodName} {cardOrAccount}?',
  },
  card: {
    id: 'account-saved-method.card',
    defaultMessage: 'card',
  },
  account: {
    id: 'account-saved-method.account',
    defaultMessage: 'account',
  },
  addedSuccessType: {
    id: 'account-saved.saved-payment.success-type',
    defaultMessage: 'Successfully added new {type} payment method.',
  },
  addedSuccess: {
    id: 'account-saved.saved-payment.success',
    defaultMessage: 'Successfully added new payment method. It may take a few minutes for it to be displayed here.',
  },
});

function SavedPayment({
  userId,
  intl: { formatMessage },
  search,
  twoFactorEnabled,
  accountOverview,
}) {
  const [paymentData, setPaymentData] = useState();
  const [baseError, setBaseError] = useState();
  const [paymentError, setError] = useState();
  const [checkout, setCheckout] = useState(null);
  const [successMethod, setSuccessMethod] = useState(null);
  const [selectedMethod, selectMethod] = useState(null);
  const [fingerprint, setFingerprint] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const urlPaymentError = get(queryString.parse(search), 'paymentError', null);
  const urlPaymentSuccess = get(queryString.parse(search), 'paymentSuccess', null);
  const urlRecurringDetailReference = get(queryString.parse(search), 'recurringDetailReference', null);

  const { locale } = window;
  const shopperLocale = GeneralUtils.getShopperLocale(locale);

  const fetchMethods = async () => {
    const { response, data } = await getMethods();

    if (response.ok) {
      setPaymentData(data);

      if (urlPaymentSuccess) {
        if (urlRecurringDetailReference) {
          const addedMethod = get(data, 'storedPaymentMethods', []).find(m => m.id === urlRecurringDetailReference);
          if (addedMethod) {
            return setSuccessMethod(addedMethod.name);
          }
        }
        return setSuccessMethod(true);
      }
      return false;
    }

    return setBaseError(true);
  };

  useEffect(() => {
    fetchMethods();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const initCheckout = async () => {
      setCheckout(await AdyenCheckout({
        locale: shopperLocale,
        environment: env.adyenApiContext,
        clientKey: env.adyenApiClientKey,
        risk: {
          enabled: false,
        },
      }));
    };

    if (!checkout) {
      initCheckout();
    }
  }, [shopperLocale, checkout]);

  useEffect(() => {
    // Set fingerprint
    if (checkout) {
      FingerprintJS.load()
        .then(fp => fp.get())
        .then((result) => {
          setFingerprint(result.visitorId);
        });
    }
  }, [checkout]);

  const remove = async (method) => {
    const confirmString = formatMessage(messages.confirmDelete, {
      paymentMethodName: method.name,
      cardOrAccount: method.type === 'scheme' ? formatMessage(messages.card) : formatMessage(messages.account),
    });

    if (window.confirm(confirmString)) { // eslint-disable-line no-alert
      const { response, data: body } = await removeMethod(method.id);
      setSuccessMethod(null);
      if (response.ok) {
        return fetchMethods();
      }

      if (body && body.error) {
        return setError(body.error);
      }
      return setError('PSC000');
    }
    return false;
  };

  const update = (method) => {
    selectMethod(method.type);
    setIsModalOpen(true);
    setSuccessMethod(null);
  };

  if (baseError) {
    return <PaymentError error="SPD001" />;
  }

  if (!paymentData) {
    if (accountOverview) return false;
    return <Loading />;
  }

  const { paymentMethods = [], storedPaymentMethods = [] } = paymentData;

  const filteredPaymentMethods = paymentMethods.filter((method) => {
    if (method.type === 'applepay') {
      try {
        if (window.ApplePaySession && window.ApplePaySession.canMakePayments()) {
          return !!get(method, 'configuration.oneClickOption');
        }
        return false;
      } catch {
        return false;
      }
    }

    return !!get(method, 'configuration.oneClickOption');
  });

  const onSuccess = (method) => {
    setIsModalOpen(false);
    if (method) {
      setSuccessMethod(method);
    }
    fetchMethods();
  };

  const selectedMethodObject = paymentMethods.find(m => m.type === selectedMethod);

  const isExpired = [];
  const expiresSoon = [];

  if (accountOverview) {
    storedPaymentMethods.forEach((card) => {
      if (card.expiryMonth && card.expiryYear && new Date(`${card.expiryYear}-${padStart(Number(card.expiryMonth) + 2, 2, '0')}-01`) <= new Date()) {
        isExpired.push(card);
      }
      if (card.expiryMonth && card.expiryYear && new Date(`${card.expiryYear}-${padStart(Number(card.expiryMonth) + 1, 2, '0')}-01`) <= new Date()) {
        expiresSoon.push(card);
      }
    });

    if (isEmpty(isExpired) && isEmpty(expiresSoon)) return false;
  }

  return (
    <React.Fragment>
      <article className={classNames('account-table account-payment', { 'error-border': accountOverview })} id="saved-payment">
        <h2 className="account-table-heading includes-button">
          <div className="title-container">
            {!accountOverview &&
              <FormattedMessage id="account.myaccount.saved-payment-methods" defaultMessage="Saved Payment Methods" />
            }
            {accountOverview &&
              <React.Fragment>
                <FontAwesomeIcon icon={faTriangleExclamation} />
                <FormattedMessage id="account.myaccount.expiring-payment-methods" defaultMessage="Payment Method Expired/Expiring" />
              </React.Fragment>
            }
          </div>
          {!accountOverview &&
            <div className="button-container">
              <button className="btn btn-line-account" onClick={() => setIsModalOpen(true)}>
                <FormattedMessage id="saved-payment.add-new" defaultMessage="Add New Payment Method" />
              </button>
            </div>
          }
        </h2>

        <PaymentContext.Provider
          value={{
            apipayment: null,
            order: {
              _id: `${userId}-save-${uuidV4()}`,
              report: { currencyCode },
              payment: { total: 0 },
            },
            isBuyItNow: false,
            fingerprint,
            setError,
            paymentError,
            paymentMethods,
            storedPaymentMethods,
            selectedMethod,
            selectMethod,
            selectedMethodObject,
            shopperLocale,
            lastUsed: null,
            items: [],
            currency: currencyCode,
            discount: 0,
            voucher: null,
            finalAmount: 0,
            voucherSaving: 0,
            transactionFee: 0,
            checkout,
            isSubscription: false,
            isNewSavedMethod: true,
            savedMethodOnSuccess: onSuccess,
            setSuccessMethod,
          }}
        >
          {isEmpty(storedPaymentMethods) &&
            <div className="account-table-row">
              <FormattedMessage id="saved-payment.no-payment-methods" defaultMessage="You currently have no saved payment methods." />
            </div>
          }
          {((paymentError || urlPaymentError) || !!successMethod) &&
            <div className="account-table-row">
              <PaymentError error={paymentError || urlPaymentError} />
              {!!successMethod &&
                <React.Fragment>
                  {typeof successMethod === 'string' ?
                    <MessageBox type="success" message={formatMessage(messages.addedSuccessType, { successMethod })} />
                    :
                    <MessageBox type="success" message={formatMessage(messages.addedSuccess)} />
                  }
                </React.Fragment>
              }
            </div>
          }
          {!accountOverview && !!storedPaymentMethods.length && storedPaymentMethods.map(method => (
            <div className="account-table-row" key={method.id}>
              <SavedPaymentMethod
                key={method.id}
                method={method}
                updateMethod={update}
                forgetMethod={remove}
              />
            </div>
          ))}
          {accountOverview && !!isExpired.length && isExpired.map(method => (
            <div className="account-table-row" key={method.id}>
              <SavedPaymentMethod
                accountOverview
                key={method.id}
                method={method}
                updateMethod={update}
                forgetMethod={remove}
              />
            </div>
          ))}
          {accountOverview && !!expiresSoon.length && expiresSoon.map(method => (
            <div className="account-table-row" key={method.id}>
              <SavedPaymentMethod
                accountOverview
                key={method.id}
                method={method}
                updateMethod={update}
                forgetMethod={remove}
              />
            </div>
          ))}

          <Modal className="account-modal" isOpen={isModalOpen} toggle={() => setIsModalOpen(false)}>
            <ModalBody>
              <div className="account-modal-title">
                <FormattedMessage id="saved-payment.add" defaultMessage="Add Payment Method" />
              </div>
              {!twoFactorEnabled &&
                <div className="account-modal-form w-100">
                  <p className="account-modal-paragraph">
                    <Link to="/account/login">
                      <FormattedMessage id="saved-payment.2fa" defaultMessage="Turn on Two Factor Authentication on your account to enable more saved payment methods" />
                    </Link>
                  </p>
                </div>
              }

              <div className="payment-details-container">
                <div className="payment-options">
                  {filteredPaymentMethods.map(method =>
                    <PaymentMethod
                      key={method.type}
                      method={method}
                      selected={selectedMethod === method.type}
                    />,
                  )}
                </div>
              </div>
            </ModalBody>

            <div className="close-button-container">
              <button aria-label="close" onClick={() => setIsModalOpen(false)}>
                <FontAwesomeIcon icon={faXmark} title="Close modal" />
              </button>
            </div>
          </Modal>
        </PaymentContext.Provider>
      </article>
    </React.Fragment>
  );
}

const mapStateToProps = ({
  router: { location: { search } }, // eslint-disable-line camelcase
}) => ({
  search,
});

export default connect(mapStateToProps, null)(injectIntl(SavedPayment));

SavedPayment.propTypes = {
  userId: PropTypes.string.isRequired,
  twoFactorEnabled: PropTypes.bool.isRequired,
  search: PropTypes.string.isRequired,
  accountOverview: PropTypes.bool,
};

SavedPayment.defaultProps = {
  accountOverview: false,
};
